import { useReducedMotion } from 'motion/react';
import { useEffect, useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { useI18nContext } from '@/i18n/i18n-react';
import { cn } from '@/lib/utils';
import type { SlideProps } from '@/lib/whats-new/edition-types';
import { useEstreiaFacts } from '@/components/whats-new/editions/estreia/use-estreia-facts';
import {
  analyticsVM,
  type AnalyticsTabView,
} from '@/components/whats-new/editions/estreia/view-models';
import { DeltaBadge } from '@/components/whats-new/slides/delta-badge';
import { Eyebrow } from '@/components/whats-new/slides/eyebrow';
import { GlassCard } from '@/components/whats-new/slides/glass-card';
import { LineSkeleton } from '@/components/whats-new/slides/line-skeleton';
import { Rise } from '@/components/whats-new/slides/rise';
import { SampleBadge } from '@/components/whats-new/slides/sample-badge';
import {
  SlideLayout,
  SlideLead,
  SlideTitle,
} from '@/components/whats-new/slides/slide-layout';
import { WordReveal } from '@/components/whats-new/slides/word-reveal';

/** The bars grow once the card has come in. */
export const ANALYTICS_START_MS = 700;
/** The indicator moves to the next tab every 2.1 s (NEWS-33). */
export const ANALYTICS_TAB_MS = 2100;

/** Height of a bar before it grows, and the least it shows (in %). */
const MIN_BAR_PCT = 4;

// Arbitrary properties: Tailwind's arbitrary duration and easing classes
// clash with tailwindcss-animate's and generate nothing.
const EASE =
  '[transition-duration:550ms] [transition-timing-function:cubic-bezier(0.16,1,0.3,1)]';

/** A KPI's value line; its skeleton keeps the same line box (NEWS-26). */
const KPI_VALUE =
  'mb-1.5 mt-[3px] text-[19px] font-semibold tabular-nums tracking-[-0.02em] max-md:text-base';

const PREVIOUS_BAR = 'bg-white/[.14]';
const CURRENT_BAR = 'bg-[linear-gradient(180deg,#6FA0FF,#155DFC)]';
const TODAY_BAR =
  'bg-[repeating-linear-gradient(135deg,rgba(111,160,255,0.95)_0_4px,rgba(111,160,255,0.35)_4px_8px)]';

/**
 * The tab on show: the first one once the card is in, then the next every
 * 2.1 s, round and round. Stays on the first, already grown, under reduced
 * motion (NEWS-09); leaving the slide stops it.
 */
function useTabCycle(count: number) {
  const reduceMotion = useReducedMotion() ?? false;
  const [tab, setTab] = useState(0);
  const [grown, setGrown] = useState(reduceMotion);

  useEffect(() => {
    setTab(0);
    if (reduceMotion) {
      setGrown(true);
      return;
    }
    setGrown(false);
    let interval: ReturnType<typeof setInterval> | undefined;
    const timer = setTimeout(() => {
      setGrown(true);
      interval = setInterval(
        () => setTab((current) => (current + 1) % count),
        ANALYTICS_TAB_MS
      );
    }, ANALYTICS_START_MS);
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [count, reduceMotion]);

  return { tab, grown };
}

function Bars({ tab, grown }: { tab: AnalyticsTabView; grown: boolean }) {
  const { LL } = useI18nContext();
  if (tab.bars.state === 'loading') {
    return (
      <Skeleton
        data-testid="analytics-bars-loading"
        className="mt-4 h-[120px] w-full bg-white/[.08] max-md:h-[84px] [@media(min-width:768px)_and_(max-height:800px)]:h-[100px]"
      />
    );
  }
  const { pairs } = tab.bars.value;
  const height = (value: number) =>
    grown ? `${Math.max(MIN_BAR_PCT, value * 100)}%` : `${MIN_BAR_PCT}%`;
  return (
    <div
      data-testid="analytics-bars"
      className="mt-4 flex h-[120px] items-end gap-[13px] max-md:h-[84px] max-md:gap-2 [@media(min-width:768px)_and_(max-height:800px)]:h-[100px]"
    >
      {pairs.map((pair, i) => {
        const today = i === pairs.length - 1;
        return (
          <div
            key={i}
            data-testid="analytics-bar-pair"
            data-today={today || undefined}
            aria-label={
              today
                ? LL.whatsNew.editions.estreia.analytics.legend.today()
                : undefined
            }
            className="flex h-full flex-1 items-end gap-1.5 max-md:gap-1"
          >
            <span
              data-bar="previous"
              className={cn(
                'block flex-1 rounded-[4px_4px_2px_2px] transition-[height]',
                EASE,
                PREVIOUS_BAR
              )}
              style={{ height: height(pair.previous ?? 0) }}
            />
            <span
              data-bar={today ? 'today' : 'current'}
              className={cn(
                'block flex-1 rounded-[4px_4px_2px_2px] transition-[height]',
                EASE,
                today ? TODAY_BAR : CURRENT_BAR
              )}
              style={{ height: height(pair.current) }}
            />
          </div>
        );
      })}
    </div>
  );
}

/**
 * 6 · Analytics (NEWS-33): the 5 real tabs with an indicator passing through
 * them every 2.1 s; each tab shows its 3 numbers against the previous
 * period and 7 pairs of bars (previous × current, the last one hatched:
 * today, still going). A number without a value reads "—" with no badge; a
 * tab without any data draws the sample bars with "Exemplo".
 */
export function AnalyticsSlide(_props: SlideProps) {
  const { LL, locale } = useI18nContext();
  const { tabs } = analyticsVM(useEstreiaFacts(), locale);
  const { tab: index, grown } = useTabCycle(tabs.length);
  const tab = tabs[index];

  return (
    <SlideLayout
      copy={
        <>
          <Rise index={0}>
            <Eyebrow lead={LL.whatsNew.editions.estreia.analytics.eyebrow()} />
          </Rise>
          <SlideTitle>
            <WordReveal text={LL.whatsNew.editions.estreia.analytics.title()} />
          </SlideTitle>
          <Rise index={2}>
            <SlideLead>
              {LL.whatsNew.editions.estreia.analytics.lead()}
            </SlideLead>
          </Rise>
        </>
      }
      visual={
        <Rise index={1}>
          <GlassCard className="px-[18px] pb-[18px] pt-4 max-md:px-3.5 max-md:pb-3.5">
            <div
              data-testid="analytics-tabs"
              className="relative mb-4 flex gap-1 rounded-[10px] bg-white/[.07] p-1"
            >
              <span
                aria-hidden="true"
                className={cn(
                  'absolute bottom-1 left-1 top-1 rounded-[7px] bg-white transition-transform',
                  EASE
                )}
                style={{
                  // 5 equal tabs, 4px apart.
                  width: `calc((100% - 8px - ${(tabs.length - 1) * 4}px) / ${tabs.length})`,
                  transform: `translateX(calc(${index} * (100% + 4px)))`,
                }}
              />
              {tabs.map((t, i) => (
                <span
                  key={t.id}
                  data-testid="analytics-tab"
                  data-active={i === index || undefined}
                  className={cn(
                    'relative z-[1] flex-1 whitespace-nowrap px-1 py-[7px] text-center text-xs font-semibold transition-colors max-md:px-0.5 max-md:text-[10.5px]',
                    i === index ? 'text-[#15191E]' : 'text-white/[.64]'
                  )}
                >
                  {t.label(LL)}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-2">
              {tab.kpis.map((kpi, i) => (
                <div
                  key={`${tab.id}-${i}`}
                  data-testid="analytics-kpi"
                  className="min-w-0 rounded-[10px] bg-white/[.06] p-3 max-md:p-2.5"
                >
                  <p className="truncate text-[11.5px] text-white/[.64] max-md:text-[10.5px]">
                    {kpi.label(LL)}
                  </p>
                  {kpi.value.state === 'ready' ? (
                    <p
                      data-testid="analytics-kpi-value"
                      className={cn(KPI_VALUE, 'truncate')}
                    >
                      {kpi.value.value}
                    </p>
                  ) : (
                    <div className={KPI_VALUE}>
                      <LineSkeleton
                        data-testid="analytics-kpi-loading"
                        className="h-6 w-16 bg-white/[.12]"
                      />
                    </div>
                  )}
                  <span className="flex h-[22px] items-center">
                    {kpi.delta && (
                      <DeltaBadge
                        current={kpi.delta.current}
                        previous={kpi.delta.previous}
                      />
                    )}
                  </span>
                </div>
              ))}
            </div>

            <Bars tab={tab} grown={grown} />

            <div className="mt-2.5 flex flex-wrap items-center gap-x-3.5 gap-y-1.5 text-[11px] text-white/[.64] max-md:text-[10.5px]">
              {(
                [
                  [
                    CURRENT_BAR,
                    LL.whatsNew.editions.estreia.analytics.legend.current(),
                  ],
                  [
                    PREVIOUS_BAR,
                    LL.whatsNew.editions.estreia.analytics.legend.previous(),
                  ],
                  [
                    TODAY_BAR,
                    LL.whatsNew.editions.estreia.analytics.legend.today(),
                  ],
                ] as const
              ).map(([swatch, text]) => (
                <span
                  key={text}
                  data-testid="analytics-legend"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap"
                >
                  <i
                    className={cn(
                      'inline-block size-2.5 rounded-[3px]',
                      swatch
                    )}
                  />
                  {text}
                </span>
              ))}
              {tab.bars.state === 'ready' && tab.bars.value.sample && (
                <SampleBadge className="ml-auto" />
              )}
            </div>
          </GlassCard>
        </Rise>
      }
    />
  );
}
